import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Music2, Mail, Phone, MapPin, Heart } from 'lucide-react';

const OPENING_HOURS = [
    { days: 'Lun – Sab', hours: '9:00 – 20:00', closed: false },
    { days: 'Domenica', hours: 'Chiuso', closed: true },
];

const Footer = () => {
    const currentYear = new Date().getFullYear();

    return (
        <footer className="text-white pt-16 pb-8" style={{ background: 'linear-gradient(135deg, #4a2c2a 0%, #3a1f1d 100%)' }}>
            <div className="container mx-auto px-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">

                    {/* Brand */}
                    <div>
                        <div className="flex items-center gap-3 mb-6">
                            <div className="gradient-pink w-12 h-12 rounded-full flex items-center justify-center shadow-lg">
                                <span className="text-white font-heading font-bold text-xl italic">M</span>
                            </div>
                            <div>
                                <h2 className="font-heading text-xl font-bold italic text-white">Madame Afro Mayor</h2>
                                <p className="text-xs text-white/50 uppercase tracking-widest">Salone di bellezza</p>
                            </div>
                        </div>
                        <p className="text-white/60 mb-6 text-sm leading-relaxed">
                            Specializzate nella cura e nello styling dei capelli afro.
                            Combiniamo tradizione africana e innovazione moderna.
                        </p>
                        <div className="flex gap-3">
                            {[
                                { Icon: Facebook, href: 'https://www.facebook.com/profile.php?id=61585322442757' },
                                { Icon: Instagram, href: 'https://www.instagram.com/madame.afromayor/' },
                                { Icon: Music2, href: 'https://www.tiktok.com/@madameafromayor' },
                                { Icon: Mail, href: 'mailto:azienda08madame.afromayor@gmail.com' },
                            ].map(({ Icon, href }, i) => (
                                <a key={i} href={href} target="_blank" rel="noopener noreferrer"
                                    className="p-2.5 bg-white/10 rounded-xl hover:gradient-pink transition-all hover:scale-110">
                                    <Icon className="w-4 h-4" />
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Link rapidi */}
                    <div>
                        <h3 className="font-heading text-lg font-bold mb-6 text-white">Link Rapidi</h3>
                        <ul className="space-y-3">
                            {[
                                { to: '/', label: 'Home' },
                                { to: '/about', label: 'Chi Siamo' },
                                { to: '/salon', label: 'Il Salone' },
                                { to: '/staff', label: 'Il Nostro Staff' },
                                { to: '/services', label: 'Servizi' },
                                { to: '/products', label: 'Prodotti' },
                                { to: '/gallery', label: 'Galleria' },
                                { to: '/contact', label: 'Contatti' },
                            ].map(l => (
                                <li key={l.to}>
                                    <Link to={l.to} className="text-white/60 hover:text-secondary-pink transition-colors text-sm flex items-center gap-2 group">
                                        <span className="w-1 h-1 rounded-full bg-primary-pink opacity-0 group-hover:opacity-100 transition-opacity" />
                                        {l.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Servizi */}
                    <div>
                        <h3 className="font-heading text-lg font-bold mb-6 text-white">I Nostri Servizi</h3>
                        <ul className="space-y-3 text-sm text-white/60">
                            {['Acconciature Afro', 'Treccine Box Braids', 'Trattamenti Naturali', 'Colorazioni', 'Consulenze Personalizzate'].map(s => (
                                <li key={s} className="hover:text-secondary-pink cursor-pointer transition-colors flex items-center gap-2 group">
                                    <span className="w-1 h-1 rounded-full bg-secondary-pink opacity-0 group-hover:opacity-100 transition-opacity" />
                                    {s}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contatti */}
                    <div>
                        <h3 className="font-heading text-lg font-bold mb-6 text-white">Contatti</h3>
                        <div className="space-y-4">
                            <div className="flex items-start gap-3">
                                <MapPin className="w-4 h-4 text-secondary-pink mt-1 flex-shrink-0" />
                                <span className="text-white/60 text-sm">Via Alfredo Cappellini 36<br />00185 Roma, Italia</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <Phone className="w-4 h-4 text-secondary-pink flex-shrink-0" />
                                <span className="text-white/60 text-sm">+39 338 652 0066</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <Mail className="w-4 h-4 text-secondary-pink flex-shrink-0" />
                                <span className="text-white/60 text-sm">azienda08madame.afromayor@gmail.com</span>
                            </div>
                        </div>
                        <div className="mt-6 p-4 bg-white/5 rounded-xl border border-white/10">
                            <h4 className="font-semibold mb-3 text-sm text-white">Orari di Apertura</h4>
                            <div className="text-xs text-white/50 space-y-1.5">
                                {OPENING_HOURS.map(({ days, hours, closed }) => (
                                    <div key={days} className="flex justify-between">
                                        <span>{days}</span>
                                        <span className={closed ? 'text-secondary-pink' : 'text-white/70'}>{hours}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="border-t border-white/10 mt-12 pt-8">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <p className="text-white/40 text-sm">
                            &copy; {currentYear} Madame Afro Mayor. Tutti i diritti riservati.
                        </p>
                        <div className="flex gap-6">
                            {[{ to: '/privacy', l: 'Privacy Policy' }, { to: '/terms', l: 'Termini' }, { to: '/shipping', l: 'Spedizioni' }].map(i => (
                                <Link key={i.to} to={i.to} className="text-white/40 hover:text-secondary-pink text-xs transition-colors">{i.l}</Link>
                            ))}
                        </div>
                    </div>
                    <p className="text-center text-white/30 text-xs mt-4 flex items-center justify-center gap-1">
                        Made with <Heart className="w-3 h-3 text-primary-pink fill-primary-pink" /> in Italia
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;